import { useState, useEffect } from 'react'
import Logo from '../logo-black.svg'
import LogoWhite from '../logo.svg'
import {
  HeaderLeftWrapper,
  HeaderRightWrapper,
  HeaderVerticalDivider,
  HeaderWrapper,
  LogoWrapper,
  NavBarWrapper,
  UserActionWrapper,
} from './atoms'
import { LocaleMenu } from './locale-menu'
import { MobileNav } from './components/mobile-nav'
import { UserStatus } from './user-status'
import { LOCALE } from '@foxrenderfarm/internals'
import { BrowserTip } from './components/browser-tip'
import {
  AboutLightDropDown,
  PricingLightDropDown,
  ProductsDropDown,
  ResourcesDropDown,
  SolutionsDropDown,
  SupportLightDropDown,
} from './components/light-dropdowns'
import { getLocalAppOrigin, isTransparentHeaderPath } from './components/light-dropdowns/common'

interface Props {
  /** 应用前缀，作为切换语言的根路径，如 share */
  prefix?: string
  isMultilingual?: boolean
  currentLocale: LOCALE
  currentPathname: string
  origin: string
  displayLanguages?: LOCALE[]
}

export const Header = ({
  prefix,
  isMultilingual,
  currentLocale,
  currentPathname,
  origin,
  displayLanguages,
}: Props) => {
  const [scrolled, setScrolled] = useState(false)

  const allowsTransparentHeader = isTransparentHeaderPath(currentPathname, currentLocale)

  useEffect(() => {
    const onScroll = () => setScrolled(typeof window !== 'undefined' && window.scrollY >= 100)
    if (typeof window !== 'undefined') {
      setScrolled(window.scrollY >= 100)
      window.addEventListener('scroll', onScroll, { passive: true })
      return () => window.removeEventListener('scroll', onScroll)
    }
  }, [currentPathname])

  /** 非 hero 页始终使用实心 header；hero 页仅在滚动后切换 */
  const headerScrolled = scrolled || !allowsTransparentHeader
  const useWhiteLogo = allowsTransparentHeader && !scrolled
  const mainOrigin = getLocalAppOrigin(origin, 8084)

  return (
    <HeaderWrapper $scrolled={headerScrolled}>
      <HeaderLeftWrapper>
        <LogoWrapper>
          <a href={`${mainOrigin}${currentLocale === LOCALE.EN ? '/' : `/${currentLocale}`}`}>
            <img
              src={useWhiteLogo ? LogoWhite : Logo}
              alt='Fox Renderfarm Cloud Render Farm Service'
              loading='eager'
            />
          </a>
        </LogoWrapper>
        <NavBarWrapper>
          <ProductsDropDown
            currentLocale={currentLocale}
            currentPathname={currentPathname}
            origin={origin}
          />
          <SolutionsDropDown
            currentLocale={currentLocale}
            currentPathname={currentPathname}
            origin={origin}
          />
          <PricingLightDropDown
            currentLocale={currentLocale}
            currentPathname={currentPathname}
            origin={origin}
          />
          <ResourcesDropDown
            currentLocale={currentLocale}
            currentPathname={currentPathname}
            origin={origin}
          />
          <SupportLightDropDown
            currentLocale={currentLocale}
            currentPathname={currentPathname}
            origin={origin}
          />
          <AboutLightDropDown
            currentLocale={currentLocale}
            currentPathname={currentPathname}
            origin={origin}
          />
        </NavBarWrapper>
      </HeaderLeftWrapper>
      <HeaderRightWrapper>
        {isMultilingual && (
          <>
            <LocaleMenu
              currentLocale={currentLocale}
              displayLanguages={displayLanguages}
              prefix={prefix}
            />
            <HeaderVerticalDivider $scrolled={headerScrolled} />
          </>
        )}
        <UserActionWrapper>
          <UserStatus locale={currentLocale} />
        </UserActionWrapper>
        <MobileNav
          currentLocale={currentLocale}
          currentPathname={currentPathname}
          origin={origin}
        />
      </HeaderRightWrapper>
      <BrowserTip />
    </HeaderWrapper>
  )
}
